import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { Mail, Compass, CheckCircle, AlertCircle } from "lucide-react";

export default function Contact() {
  const { t } = useTranslation();
  const topics = t("contact.topics", { returnObjects: true }) || [];
  const [formData, setFormData] = useState({ name: "", email: "", topic: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState({ type: null, message: "" });
  const [contactEmail, setContactEmail] = useState("hello@thethrivinglife101.com");

  useEffect(() => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
    axios.get(`${backendUrl}/api/config`)
      .then((res) => { if (res.data?.contact_email) setContactEmail(res.data.contact_email); })
      .catch(() => {});
  }, []);

  // Keep the default topic in sync with the current language.
  useEffect(() => {
    setFormData((prev) => ({ ...prev, topic: prev.topic || (topics[0] || "") }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.email.includes("@") || !formData.message) {
      setStatus({ type: "error", message: t("contact.invalid") });
      return;
    }
    setSubmitting(true);
    setStatus({ type: null, message: "" });
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
      const response = await axios.post(`${backendUrl}/api/contact`, formData);
      if (response.data.success) {
        setStatus({ type: "success", message: t("contact.success") });
        setFormData({ name: "", email: "", topic: topics[0] || "", message: "" });
      } else {
        setStatus({ type: "error", message: t("contact.error") });
      }
    } catch (err) {
      setStatus({ type: "error", message: t("contact.error") });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="py-12 space-y-24">

      <section className="max-w-4xl mx-auto px-6 text-center space-y-6">
        <span className="text-[10px] font-sans tracking-[0.3em] uppercase text-[var(--accent-bronze)] font-semibold block">{t("contact.eyebrow")}</span>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif text-primary-custom tracking-tight leading-tight">{t("contact.heading")}</h1>
        <p className="font-serif text-base sm:text-lg text-secondary-custom max-w-2xl mx-auto leading-relaxed font-light">{t("contact.subtitle")}</p>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-24 grid grid-cols-1 lg:grid-cols-12 gap-16">
        <div className="lg:col-span-5 space-y-10">
          <div className="space-y-4">
            <h2 className="text-2xl font-serif text-primary-custom">{t("contact.leftHeading")}</h2>
            <p className="font-serif text-sm sm:text-base text-secondary-custom leading-relaxed font-light">{t("contact.leftBody")}</p>
          </div>
          <div className="space-y-6 pt-8 border-t border-subtle-custom">
            <div className="flex items-start space-x-4">
              <div className="w-10 h-10 border border-subtle-custom bg-surface-custom flex items-center justify-center shrink-0">
                <Mail className="w-4 h-4 text-[var(--accent-bronze)]" strokeWidth={1} />
              </div>
              <div className="space-y-1">
                <span className="text-[9px] font-sans text-secondary-custom uppercase tracking-widest block">{t("contact.byEmail")}</span>
                <a href={`mailto:${contactEmail}`} className="font-serif text-sm text-primary-custom font-medium hover:text-[var(--accent-bronze)] transition-colors duration-300" data-testid="contact-email-link">{contactEmail}</a>
              </div>
            </div>
            <div className="flex items-start space-x-4">
              <div className="w-10 h-10 border border-subtle-custom bg-surface-custom flex items-center justify-center shrink-0">
                <Compass className="w-4 h-4 text-[var(--accent-bronze)]" strokeWidth={1} />
              </div>
              <div className="space-y-1">
                <span className="text-[9px] font-sans text-secondary-custom uppercase tracking-widest block">{t("contact.ourReply")}</span>
                <span className="font-serif text-sm text-primary-custom">{t("contact.ourReplyBody")}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          <div className="border border-subtle-custom bg-surface-custom/20 p-8 md:p-12 shadow-md rounded-none">
            <form onSubmit={handleSubmit} className="space-y-8" noValidate>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-2">
                  <label className="text-[9px] font-sans tracking-widest text-secondary-custom uppercase block">{t("contact.fullName")}</label>
                  <input type="text" name="name" value={formData.name} onChange={handleInputChange} disabled={submitting} data-testid="contact-name-input" placeholder={t("contact.namePlaceholder")} className="w-full bg-transparent border-b border-focus-custom py-2 text-xs tracking-wider text-primary-custom uppercase outline-none focus:outline-none placeholder:text-secondary-custom/40" required />
                </div>
                <div className="space-y-2">
                  <label className="text-[9px] font-sans tracking-widest text-secondary-custom uppercase block">{t("contact.emailAddress")}</label>
                  <input type="email" name="email" value={formData.email} onChange={handleInputChange} disabled={submitting} data-testid="contact-email-input" placeholder={t("contact.emailPlaceholder")} className="w-full bg-transparent border-b border-focus-custom py-2 text-xs tracking-wider text-primary-custom uppercase outline-none focus:outline-none placeholder:text-secondary-custom/40" required />
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-[9px] font-sans tracking-widest text-secondary-custom uppercase block">{t("contact.topic")}</label>
                <select name="topic" value={formData.topic} onChange={handleInputChange} disabled={submitting} data-testid="contact-topic-select" className="w-full bg-surface-custom border border-subtle-custom p-3 text-xs tracking-wider text-primary-custom uppercase outline-none focus:outline-none focus:border-[var(--accent-bronze)]">
                  {topics.map((tp) => (<option key={tp} value={tp}>{tp}</option>))}
                </select>
              </div>
              <div className="space-y-2">
                <label className="text-[9px] font-sans tracking-widest text-secondary-custom uppercase block">{t("contact.yourMessage")}</label>
                <textarea name="message" value={formData.message} onChange={handleInputChange} disabled={submitting} data-testid="contact-message-input" placeholder={t("contact.messagePlaceholder")} rows={5} className="w-full bg-transparent border border-subtle-custom p-4 text-xs tracking-wider text-primary-custom uppercase outline-none focus:outline-none focus:border-[var(--accent-bronze)] placeholder:text-secondary-custom/40" required />
              </div>
              <div className="space-y-4">
                <button type="submit" disabled={submitting} data-testid="contact-submit-button" className="w-full flex items-center justify-center space-x-3 border border-primary bg-primary-custom text-primary-custom hover:bg-[var(--accent-bronze)] hover:text-white hover:border-[var(--accent-bronze)] py-4 text-[10px] uppercase tracking-[0.25em] font-semibold transition-all duration-300 rounded-none shadow-sm">
                  {submitting ? t("contact.sending") : t("contact.send")}
                </button>
                {status.type && (
                  <div data-testid="contact-status-message" className={`flex items-center justify-center space-x-2 text-xs pt-2 transition-all duration-300 ${status.type === "success" ? "text-emerald-600 dark:text-emerald-400 font-serif italic" : "text-red-500 dark:text-red-400"}`}>
                    {status.type === "success" ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                    <span>{status.message}</span>
                  </div>
                )}
              </div>
            </form>
          </div>
        </div>
      </section>

    </div>
  );
}
